import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../../../utils/api';
import ErrorModal from '../../../modals/ErrorModal';

const UploadPhotosModal = ({ isOpen, onClose, onInsert }) => {
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [insertingPhotos, setInsertingPhotos] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const newPhotos = [];
      
      for (const file of files) {
        // Create a unique ID and temporary blob URL for preview
        const photoId = `uploaded_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const previewUrl = URL.createObjectURL(file);
        
        const photo = {
          id: photoId,
          name: file.name,
          url: previewUrl,        // Temporary preview URL
          previewUrl: previewUrl, // Keep reference for cleanup
          file: file,             // Keep file reference for upload
          size: file.size,
          type: file.type,
          uploaded_at: new Date().toISOString(),
          isUploaded: false       // Track if uploaded to GCS
        };
        
        newPhotos.push(photo);
      }
      
      setUploadedPhotos(prev => [...prev, ...newPhotos]);
      
    } catch (error) {
      console.error('Error processing photos:', error);
      setErrorModal({
        isOpen: true,
        message: 'Error processing photos. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const selectAll = () => {
    setSelectedPhotos(uploadedPhotos.map(photo => photo.id));
  };

  const deselectAll = () => {
    setSelectedPhotos([]);
  };

  const removePhoto = (photoId) => {
    // Remove from uploaded photos
    const photoToRemove = uploadedPhotos.find(photo => photo.id === photoId);
    if (photoToRemove && photoToRemove.previewUrl) {
      URL.revokeObjectURL(photoToRemove.previewUrl);
    }
    
    setUploadedPhotos(prev => prev.filter(photo => photo.id !== photoId));
    setSelectedPhotos(prev => prev.filter(id => id !== photoId));
  };

  const handleInsert = async () => {
    const photosToInsert = uploadedPhotos.filter(photo => selectedPhotos.includes(photo.id));
    
    if (photosToInsert.length === 0) return;
    
    setInsertingPhotos(true);
    
    try {
      const token = localStorage.getItem('token');
      const uploadedResults = [];
      const errors = [];
      
      // Upload each selected photo to GCS
      for (const photo of photosToInsert) {
        try {
          const formData = new FormData();
          formData.append('file', photo.file);
          
          const response = await axios.post(
            `${API}/admin/reports/upload-photo`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          if (response.data.success) {
            // Create photo object with GCS URL
            uploadedResults.push({
              id: photo.id,
              name: photo.name,
              url: response.data.gcs_url,
              gcs_url: response.data.gcs_url,
              size: photo.size,
              type: photo.type,
              uploaded_at: photo.uploaded_at,
              isUploaded: true
            });
            
            // Revoke the blob URL since we now have GCS URL
            if (photo.previewUrl) {
              URL.revokeObjectURL(photo.previewUrl);
            }
          }
        } catch (uploadError) {
          const errorMessage = uploadError.response?.data?.detail || `Failed to upload ${photo.name}`;
          errors.push(errorMessage);
          console.error(`Failed to upload ${photo.name}:`, errorMessage);
        }
      }
      
      // If we have errors, show them
      if (errors.length > 0) {
        if (uploadedResults.length === 0) {
          // All failed
          setErrorModal({
            isOpen: true,
            message: errors[0] // Show first error (likely storage limit)
          });
          setInsertingPhotos(false);
          return;
        } else {
          // Partial success
          setErrorModal({
            isOpen: true,
            message: `${uploadedResults.length} photo(s) uploaded successfully. ${errors.length} failed: ${errors[0]}`
          });
        }
      }
      
      // Insert successfully uploaded photos
      if (uploadedResults.length > 0) {
        onInsert(uploadedResults);
      }
      
      // Clean up: revoke URLs for non-selected photos
      uploadedPhotos.forEach(photo => {
        if (!selectedPhotos.includes(photo.id) && photo.previewUrl) {
          URL.revokeObjectURL(photo.previewUrl);
        }
      });
      
    } catch (error) {
      console.error('Error uploading photos:', error);
      setErrorModal({
        isOpen: true,
        message: 'Error uploading photos. Please try again.'
      });
    } finally {
      setInsertingPhotos(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleClose = () => {
    // Clean up all blob URLs on close
    uploadedPhotos.forEach(photo => {
      if (photo.previewUrl) {
        URL.revokeObjectURL(photo.previewUrl);
      }
    });
    setUploadedPhotos([]);
    setSelectedPhotos([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">📁 Upload Additional Photos</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload and select photos to include in the report
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={insertingPhotos}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Upload Section */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Upload Photos</h3>
            <label className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer transition-colors ${(uploading || insertingPhotos) ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {uploading ? 'Processing...' : 'Choose Files'}
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading || insertingPhotos}
                className="hidden"
              />
            </label>
          </div>
          
          <p className="text-sm text-gray-600">
            Select multiple image files to upload. Supported formats: JPG, PNG, GIF, WebP • Max 10MB each
          </p>
        </div>

        {/* Controls */}
        {uploadedPhotos.length > 0 && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  {uploadedPhotos.length} photos ready
                </span>
                <span className="text-sm text-green-600">
                  {selectedPhotos.length} selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={selectAll}
                  disabled={uploadedPhotos.length === 0 || insertingPhotos}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAll}
                  disabled={selectedPhotos.length === 0 || insertingPhotos}
                  className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Deselect All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {uploading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600">Processing photos...</span>
            </div>
          )}

          {!uploading && uploadedPhotos.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-gray-600 font-medium">No photos uploaded yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Use the "Choose Files" button above to upload photos
              </p>
            </div>
          )}

          {!uploading && uploadedPhotos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {uploadedPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    selectedPhotos.includes(photo.id)
                      ? 'border-green-500 ring-2 ring-green-200 transform scale-105'
                      : 'border-gray-200 hover:border-green-300 hover:shadow-md'
                  } ${insertingPhotos ? 'opacity-50 pointer-events-none' : ''}`}
                  onClick={() => !insertingPhotos && togglePhotoSelection(photo.id)}
                >
                  <img
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-opacity duration-200"></div>
                  
                  {selectedPhotos.includes(photo.id) && (
                    <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                      ✓
                    </div>
                  )}
                  
                  {/* Remove button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!insertingPhotos) removePhoto(photo.id);
                    }}
                    disabled={insertingPhotos}
                    className="absolute top-2 left-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors shadow-lg disabled:opacity-50"
                    title="Remove photo"
                  >
                    ×
                  </button>
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-white text-xs font-medium truncate">{photo.name}</p>
                    <p className="text-white text-xs opacity-75">{formatFileSize(photo.size)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <span className="text-sm text-gray-600">
            {selectedPhotos.length} photo(s) selected
          </span>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              disabled={insertingPhotos}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              disabled={selectedPhotos.length === 0 || insertingPhotos}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {insertingPhotos ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                `Insert Selected (${selectedPhotos.length})`
              )}
            </button>
          </div>
        </div>
      </div>
      
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Upload Error"
        message={errorModal.message}
      />
    </div>
  );
};

export default UploadPhotosModal;